// ============================================================================
// Sentiment Utilities
// Centralized sentiment normalization and display functions
// ============================================================================

/**
 * Normalized sentiment types used throughout the application
 */
export type SentimentValue = "positive" | "negative" | "neutral";

/**
 * Normalize raw sentiment string to standard form.
 * Handles both short codes ("p", "n") and full words ("positive", "negative").
 * @example normalizeSentiment("p") => "positive"
 * @example normalizeSentiment("POSITIVE") => "positive"
 * @example normalizeSentiment("n") => "negative"
 * @example normalizeSentiment("unknown") => "neutral"
 */
export function normalizeSentiment(sentiment: string | null | undefined): SentimentValue {
  if (!sentiment) return "neutral";

  const s = sentiment.toLowerCase().trim();

  if (s === "p" || s === "positive") return "positive";
  if (s === "n" || s === "negative") return "negative";
  return "neutral";
}

/**
 * Check if sentiment is positive
 * @example isPositive("p") => true
 * @example isPositive("positive") => true
 */
export function isPositive(sentiment: string | null | undefined): boolean {
  return normalizeSentiment(sentiment) === "positive";
}

/**
 * Check if sentiment is negative
 * @example isNegative("n") => true
 * @example isNegative("negative") => true
 */
export function isNegative(sentiment: string | null | undefined): boolean {
  return normalizeSentiment(sentiment) === "negative";
}

/**
 * Convert sentiment to human-readable label
 * @example getSentimentLabel("p") => "Positive"
 * @example getSentimentLabel("negative") => "Negative"
 */
export function getSentimentLabel(sentiment: string | null | undefined): string {
  const normalized = normalizeSentiment(sentiment);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/**
 * Get Tailwind CSS classes for sentiment badge styling
 * @example getSentimentBadgeClasses("p") => "bg-green-400/10 text-green-400 border-green-400/20"
 */
export function getSentimentBadgeClasses(sentiment: string | null | undefined): string {
  const normalized = normalizeSentiment(sentiment);

  switch (normalized) {
    case "positive":
      return "bg-green-400/10 text-green-400 border-green-400/20";
    case "negative":
      return "bg-red-400/10 text-red-400 border-red-400/20";
    case "neutral":
      return "bg-gray-400/10 text-gray-400 border-gray-400/20";
  }
}

/**
 * Get background gradient class based on sentiment (for card backgrounds)
 * @example getSentimentGradientClass("positive") => "from-green-500/10"
 */
export function getSentimentGradientClass(sentiment: string | null | undefined): string {
  return isPositive(sentiment) ? "from-green-500/10" : "from-red-500/10";
}
